import Vue from "vue";
import VueRouter from "vue-router";
import { Message } from "element-ui";
import store from "@/vuex/store.js";
import { getToken, isManager } from "@/utils/auth.js";
import { requiredRoutePermissions } from "@/utils/pagePermissionRecovery.js";
import {
  DEVICE_MONITOR_PERMISSION_KEYS,
  PAGE_PERMISSION,
} from "@/config/permissions.js";
import { DENTAL_LAYOUT_TYPE } from "@/config/enums.js";
Vue.use(VueRouter);

import Home from "../page/homePage.vue";
import Login from "../page/loginPage.vue";
import AlarmCenter from "../page/alarmPage/alarmCenter.vue";

// 解决报错
const originalPush = VueRouter.prototype.push;
const originalReplace = VueRouter.prototype.replace;
// 针对 push 方法
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject)
    return originalPush.call(this, location, onResolve, onReject);
  return originalPush.call(this, location).catch((err) => err);
};
// 针对 replace 方法
VueRouter.prototype.replace = function push(location, onResolve, onReject) {
  if (onResolve || onReject)
    return originalReplace.call(this, location, onResolve, onReject);
  return originalReplace.call(this, location).catch((err) => err);
};

// 创建路由实例
const router = new VueRouter({
  // history 模式：URL 去掉 #，形如 /platOverview（更主流、服务端能记录路径）。
  // 依赖各环境 nginx 的 SPA fallback：location / { try_files $uri $uri/ /index.html; }
  // 否则刷新/直接访问子路由会 404。122 已配；部署到 47 前需确认其 nginx 同样有该回退。
  mode: "history",
  routes: [
    {
      path: "/",
      redirect: "/login",
      hidden: true,
    },
    {
      path: "/login",
      component: Login,
      name: "",
      hidden: true,
      meta: { public: true },
    },
    {
      path: "/largeScreen",
      component: () => import("@/page/largeScreen.vue"),
      meta: {
        permissionByQuery: {
          monitor: PAGE_PERMISSION.SCREEN_MONITOR,
          gis: PAGE_PERMISSION.SCREEN_GIS,
        },
      },
    },
    {
      path: "/standardTemplate",
      component: () => import("@/page/standardTemplate.vue"),
      meta: { permission: PAGE_PERMISSION.SCADA_TEMPLATE },
    },
    {
      path: "/home", //路径
      name: "", //命名
      component: Home, //注册的页面
      children: [
        //   平台概况（dispatcher：按账号渲染通用或定制概况，见 customProfiles.js）
        {
          path: "/platOverview",
          name: "",
          component: () => import("@/page/platOverviewDispatch.vue"),
          meta: { permission: PAGE_PERMISSION.PLATFORM_OVERVIEW },
        },
        //   大屏中心（菜单项，点击时打开新标签跳转 /largeScreen）
        //   主窗口本身保持在 /platOverview，避免路由残留空白页
        {
          path: "/glsLargeScreen",
          redirect: "/platOverview",
        },
        {
          path: "/largeScreen/monitor",
          redirect: "/platOverview",
        },
        {
          path: "/largeScreen/gis",
          redirect: "/platOverview",
        },
        // 云组态
        {
          path: "/cloudComponents",
          name: "",
          component: () => import("@/page/cloudComponents.vue"),
          meta: {
            anyPermissions: [
              PAGE_PERMISSION.SCADA_TEMPLATE,
              PAGE_PERMISSION.SCADA_BUILDING,
              PAGE_PERMISSION.SCADA_PREPARATION_WATER,
              PAGE_PERMISSION.SCADA_DENTAL_DISINFECTION,
            ],
          },
          children: [
            //   组态模板库
            {
              path: "templateGallery",
              name: "",
              component: () => import("@/page/templateGallery.vue"),
              meta: { permission: PAGE_PERMISSION.SCADA_TEMPLATE },
            },
            {
              path: "buildingLayouts",
              name: "",
              component: () => import("@/page/buildingLayouts.vue"),
              meta: { permission: PAGE_PERMISSION.SCADA_BUILDING },
            },
            {
              path: "preparationWaterQualityLayouts",
              name: "",
              component: () => import("@/page/preparationWaterQualityLayouts.vue"),
              meta: { permission: PAGE_PERMISSION.SCADA_PREPARATION_WATER },
            },
            {
              path: "dentalDisinfectionLayouts",
              name: "",
              component: () => import("@/page/dentalDisinfectionLayouts.vue"),
              meta: { permission: PAGE_PERMISSION.SCADA_DENTAL_DISINFECTION },
            },
            {
              path: "wuhanPortraitLayouts",
              name: "wuhanPortraitLayouts",
              component: () => import("@/page/dentalDisinfectionLayouts.vue"),
              props: { layoutType: DENTAL_LAYOUT_TYPE.PORTRAIT },
              meta: { permission: PAGE_PERMISSION.SCADA_DENTAL_DISINFECTION },
            },
            //   2D大楼水质组态
            {
              path: "buildingMonitor/:layoutId?",
              name: "",
              component: () => import("@/page/buildingMonitor.vue"),
              meta: { permission: PAGE_PERMISSION.SCADA_BUILDING },
            },
            //   数字化口腔诊疗用水在线管理平台（v3）预览
            {
              path: "scadaV3Preview",
              name: "",
              component: () => import("@/page/scadaV3Preview.vue"),
              meta: { permission: PAGE_PERMISSION.SCADA_TEMPLATE },
            },
            //   医疗用水智慧运维管理平台
            {
              path: "endoscopePreview",
              name: "",
              component: () => import("@/page/endoscopePreview.vue"),
              meta: { permission: PAGE_PERMISSION.SCADA_TEMPLATE },
            },
            //   内镜洗消组态（v2）预览
            {
              path: "endoscopePreviewV2",
              name: "",
              component: () => import("@/page/endoscopePreview.vue"),
              props: { variant: "v2" },
              meta: { permission: PAGE_PERMISSION.SCADA_TEMPLATE },
            },
            {
              path: "preparationWaterQualityPreview/:id?",
              name: "",
              component: () => import("@/page/preparationWaterQualityPreview.vue"),
              meta: { permission: PAGE_PERMISSION.SCADA_PREPARATION_WATER },
            },
            {
              path: "wuhanPortraitPreview/:id",
              name: "wuhanPortraitPreview",
              component: () => import("@/page/wuhanPortraitPreview.vue"),
              beforeEnter: (to, from, next) => import("@/utils/dentalLayoutRoutes.js").then(module => module.dentalLayoutGuard(to, from, next)).catch(() => next(false)),
              meta: { permission: PAGE_PERMISSION.SCADA_DENTAL_DISINFECTION },
            },
            {
              path: "dentalCombinedPreview/:id",
              name: "dentalCombinedPreview",
              component: () => import("@/page/dentalCombinedPreview.vue"),
              meta: { permission: PAGE_PERMISSION.SCADA_DENTAL_DISINFECTION },
            },
            {
              path: "dentalDisinfectionPreview/:id?",
              name: "",
              component: () => import("@/page/dentalDisinfectionPreview.vue"),
              beforeEnter: (to, from, next) => import("@/utils/dentalLayoutRoutes.js").then(module => module.dentalLayoutGuard(to, from, next)).catch(() => next(false)),
              meta: { permission: PAGE_PERMISSION.SCADA_DENTAL_DISINFECTION },
            },
          ],
        },
        // 质量控制
        {
          path: "/qualityControl/disinfectantChange",
          name: "disinfectantChange",
          component: () =>
            import("@/page/qualityControlPage/disinfectantChange.vue"),
          meta: { permission: PAGE_PERMISSION.QUALITY_DISINFECTANT_CHANGE },
        },
        {
          path: "/qualityControl/sensorCalibration",
          name: "sensorCalibration",
          component: () =>
            import("@/page/qualityControlPage/sensorCalibration.vue"),
          meta: { permission: PAGE_PERMISSION.QUALITY_SENSOR_CALIBRATION },
        },
        // 消毒中心是账号级定制页：菜单由 customProfiles 注入，访问由独立权限控制。
        {
          path: "/disinfectionRecord",
          name: "disinfectionRecord",
          component: () =>
            import("@/page/qualityControlPage/disinfectionRecord.vue"),
          meta: { permission: PAGE_PERMISSION.QUALITY_DISINFECTION_RECORD },
        },
        {
          path: "/disinfectionRecord/schedules/temporary/new",
          name: "disinfectionTemporaryCreate",
          component: () =>
            import("@/page/qualityControlPage/disinfectionTemporarySchedule.vue"),
          meta: { permission: PAGE_PERMISSION.QUALITY_DISINFECTION_RECORD },
        },
        {
          path: "/disinfectionRecord/schedules/temporary/:id",
          name: "disinfectionTemporaryEdit",
          component: () =>
            import("@/page/qualityControlPage/disinfectionTemporarySchedule.vue"),
          meta: { permission: PAGE_PERMISSION.QUALITY_DISINFECTION_RECORD },
        },
        // 在线监测 - 定时任务
        {
          path: "/onlineMonitoring/scheduledTask",
          name: "scheduledTask",
          component: () =>
            import("@/page/onlineMonitoring/scheduledTask/Index.vue"),
          meta: { permission: PAGE_PERMISSION.MONITOR_SCHEDULED_TASK },
        },
        // 报警中心
        {
          path: "/alarmCenter",
          name: "",
          component: AlarmCenter,
          meta: {
            anyPermissions: [
              PAGE_PERMISSION.ALARM_LATEST,
              PAGE_PERMISSION.ALARM_DEVICE_ONLINE,
            ],
          },
          children: [
            //   报警记录
            {
              path: "alarmRecord",
              name: "",
              component: () => import("@/page/alarmPage/alarmRecord.vue"),
              meta: { permission: PAGE_PERMISSION.ALARM_LATEST },
            },
            // 设备上下线记录
            {
              path: "deviceOnlineRecord",
              name: "",
              component: () => import("@/page/alarmPage/deviceOnlineRecord.vue"),
              meta: { permission: PAGE_PERMISSION.ALARM_DEVICE_ONLINE },
            },
          ],
        },
        // 故障反馈
        {
          path: "/faultFeedback",
          name: "",
          component: () => import("@/page/faultPage/faultList.vue"),
          meta: { permission: PAGE_PERMISSION.FAULT_FEEDBACK },
        },
        {
          path: "/faultFeedback/detail",
          name: "",
          component: () => import("@/page/faultPage/faultDetail.vue"),
          meta: { permission: PAGE_PERMISSION.FAULT_FEEDBACK },
        },
        // 数据中心
        {
          path: "/dataCenter",
          name: "",
          component: () => import("@/page/dataCenterPage/dataCenter.vue"),
          meta: { permission: PAGE_PERMISSION.DATA_HISTORY },
          children: [
            //   历史数据
            {
              path: "historyData",
              name: "",
              component: () => import("@/page/dataCenterPage/historyData.vue"),
              meta: { permission: PAGE_PERMISSION.DATA_HISTORY },
            },
          ],
        },
        // 项目中心
        {
          path: "/projectCenter",
          name: "",
          component: () => import("@/page/projectPage/projectCenter.vue"),
          meta: { permission: PAGE_PERMISSION.PROJECT_MANAGE },
          children: [
            // 项目管理
            {
              path: "projectManage",
              name: "",
              component: () => import("@/page/projectPage/projectManage.vue"),
              meta: { permission: PAGE_PERMISSION.PROJECT_MANAGE },
            },
          ],
        },
        // 设备中心
        {
          path: "/equipmentCenter",
          name: "",
          component: () => import("@/page/equipmentPage/equipmentCenter.vue"),
          meta: {
            anyPermissions: [
              ...DEVICE_MONITOR_PERMISSION_KEYS,
              PAGE_PERMISSION.DEVICE_MANAGE,
              PAGE_PERMISSION.DEVICE_POINT_TEMPLATE,
              PAGE_PERMISSION.DEVICE_FIRMWARE,
            ],
          },
          children: [
            // 设备管理
            {
              path: "equipmentManage",
              name: "equipmentManage",
              component: () =>
                import("@/page/equipmentPage/equipmentManage.vue"),
              meta: { permission: PAGE_PERMISSION.DEVICE_MANAGE },
            },
            // 设备详情
            {
              path: "equipmentDetails",
              name: "equipmentDetails",
              component: () =>
                import("@/page/equipmentPage/equipmentDetails.vue"),
              meta: {
                anyPermissions: [
                  ...DEVICE_MONITOR_PERMISSION_KEYS,
                  PAGE_PERMISSION.DEVICE_MANAGE,
                ],
              },
            },
            // 数据点模板
            {
              path: "dataPointTemplate",
              name: "dataPointTemplate",
              component: () =>
                import("@/page/equipmentPage/dataPointTemplate.vue"),
              meta: { permission: PAGE_PERMISSION.DEVICE_POINT_TEMPLATE },
            },
            // 固件管理（设备固件 / 脚本版本可观测，为 OTA 预留入口）
            {
              path: "firmwareManage",
              name: "firmwareManage",
              component: () =>
                import("@/page/equipmentPage/firmwareManage.vue"),
              meta: { permission: PAGE_PERMISSION.DEVICE_FIRMWARE },
            },
          ],
        },
        // @deprecated 物联网卡充值已永久停用：旧 URL 兼容书签，跳转概览。
        {
          path: "/iotRecharge",
          redirect: "/platOverview",
          meta: { deprecated: true },
        },
        // 视频监控
        {
          path: "/videoMonitoring",
          name: "",
          component: () => import("@/page/videoMonitoring.vue"),
        },
        // 系统管理
        {
          path: "/systemManage",
          name: "",
          component: () => import("@/page/systemManagePage/systemManage.vue"),
          meta: { permission: PAGE_PERMISSION.SYSTEM_ALARM_NOTIFY },
          children: [
            // 用户管理
            {
              path: "userManage",
              name: "",
              component: () => import("@/page/systemManagePage/userManage.vue"),
              meta: { managerOnly: true },
            },
            // 个人资料
            {
              path: "userDetails",
              name: "",
              component: () =>
                import("@/page/systemManagePage/userDetails.vue"),
              meta: { selfService: true },
            },
            // 告警通知配置
            {
              path: "alarmNotify",
              name: "",
              component: () =>
                import("@/page/systemManagePage/alarmNotify.vue"),
              meta: { permission: PAGE_PERMISSION.SYSTEM_ALARM_NOTIFY },
            },
          ],
        },
        // 日志管理
        {
          path: "/logManage",
          name: "",
          component: () => import("@/page/logManagePage/logManage.vue"),
          meta: {
            anyPermissions: [
              PAGE_PERMISSION.LOG_ACCESS,
              PAGE_PERMISSION.LOG_OPERATION,
            ],
          },
          children: [
            // 访问日志
            {
              path: "accessLog",
              name: "",
              component: () =>
                import("@/page/logManagePage/accessLog.vue"),
              meta: { permission: PAGE_PERMISSION.LOG_ACCESS },
            },
            // 操作日志
            {
              path: "operationLog",
              name: "",
              component: () =>
                import("@/page/logManagePage/operationLog.vue"),
              meta: { permission: PAGE_PERMISSION.LOG_OPERATION },
            },
          ],
        },
      ],
    },
    // 兜底：未匹配的路径（含旧的 #/xxx 书签经 nginx 落到 index.html 后）跳概览页，避免白屏
    {
      path: "*",
      redirect: "/platOverview",
    },
  ],
});

function safeLoginRedirectTarget(to) {
  const redirect = Array.isArray(to.query.redirect)
    ? to.query.redirect[0]
    : to.query.redirect
  if (typeof redirect !== 'string' || !redirect.startsWith('/') || redirect.startsWith('/login')) {
    return '/platOverview'
  }
  return redirect
}

router.beforeEach(async (to, from, next) => {
  if (to.meta.public) {
    if (to.path === '/login' && getToken()) {
      next(safeLoginRedirectTarget(to))
      return
    }
    next()
    return
  }
  if (!getToken()) {
    next(`/login?redirect=${to.fullPath}`)
    return
  }
  if (isManager() || to.meta.selfService) {
    next()
    return
  }
  if (to.meta.managerOnly) {
    Message.warning('当前账号无权访问该页面')
    next('/platOverview')
    return
  }

  try {
    await store.dispatch('permission/ensureLoaded')
    const allowed = store.getters['permission/hasAny'](requiredRoutePermissions(to))
    if (allowed) {
      next()
      return
    }
    Message.warning('当前账号无权访问该页面')
    next(to.path === '/platOverview' ? false : '/platOverview')
  } catch (error) {
    Message.error('加载页面权限失败，请稍后重试')
    next(false)
  }
})

// 全局后置守卫
router.afterEach(() => {
    // 路由切换后的操作，比如关闭loading等
})

export default router
